import { customerApiClient } from './customer-api-client';
import type { CustomerJobDetail } from '@/types/customer-job.types';
import type {
  ApproveCustomerQuotationPayload,
  RespondCustomerQuotationPayload,
} from '@/types/customer-quotation.types';

/**
 * Customer Module Phase 7 (Quotations & Documents). The download endpoint
 * streams file bytes (not the { data, meta } JSON envelope) — fetched as a
 * blob through the same authenticated customerApiClient (a plain <a href>
 * browser navigation would not carry the Bearer token), then saved
 * client-side via a temporary object URL. The server never hands out a
 * reusable signed URL; every download re-authenticates through this call.
 */
export const customerQuotationService = {
  /** D-129 — approval is multipart: Work Order number, description and PDF. */
  approve: (
    jobId: string,
    payload: ApproveCustomerQuotationPayload,
  ): Promise<CustomerJobDetail> => {
    const fd = new FormData();
    fd.append('workOrderNumber', payload.workOrderNumber);
    fd.append('workOrderDescription', payload.workOrderDescription);
    fd.append('workOrderDocument', payload.file);
    return customerApiClient
      .post<CustomerJobDetail>(`/customer-portal/jobs/${jobId}/quotation/approve`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  reject: (jobId: string, payload: RespondCustomerQuotationPayload): Promise<CustomerJobDetail> =>
    customerApiClient
      .post<CustomerJobDetail>(`/customer-portal/jobs/${jobId}/quotation/reject`, payload)
      .then((r) => r.data),

  requestModification: (
    jobId: string,
    payload: RespondCustomerQuotationPayload,
  ): Promise<CustomerJobDetail> =>
    customerApiClient
      .post<CustomerJobDetail>(
        `/customer-portal/jobs/${jobId}/quotation/request-modification`,
        payload,
      )
      .then((r) => r.data),

  async download(jobId: string, filename: string): Promise<void> {
    const response = await customerApiClient.get(
      `/customer-portal/jobs/${jobId}/quotation/document`,
      { responseType: 'blob' },
    );
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
